import { supabase } from './supabase';
import { DEFAULT_HOME_LAYOUT_CONTENT, HomepageLayoutContent } from '../components/sections/sectionRegistry';
import { validateAndNormalizeHomepageLayout } from './layoutValidation';

const EMPTY_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  sections: [],
  flows: [],
  flowState: {},
};

export interface PageLayoutRecord<T = any> {
  id?: string;
  page_key: string;
  title?: string | null;
  draft_content?: T | null;
  published_content?: T | null;
  draft_updated_at?: string | null;
  last_published_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export const PAGE_LAYOUTS_SELECT =
  'id, page_key, title, draft_content, published_content, draft_updated_at, last_published_at, created_at, updated_at';
const PAGE_LAYOUTS_SELECT_LEGACY =
  'id, page_key, draft_content, published_content, draft_updated_at, last_published_at, created_at, updated_at';

const BUILDER_API_SUPPORT_CACHE_KEY = 'builder_page_layout_api_support';
let builderApiSupported: boolean | null = null;
let builderApiProbe: Promise<boolean> | null = null;

if (typeof window !== 'undefined') {
  const cached = window.sessionStorage.getItem(BUILDER_API_SUPPORT_CACHE_KEY);
  if (cached === '1') builderApiSupported = true;
  if (cached === '0') builderApiSupported = false;
}

function cacheBuilderApiSupport(value: boolean) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(BUILDER_API_SUPPORT_CACHE_KEY, value ? '1' : '0');
}

function isBuilderApiMissingError(error: unknown): boolean {
  const code = String((error as any)?.code ?? '');
  const message = `${String((error as any)?.message ?? '')} ${String((error as any)?.details ?? '')}`.toLowerCase();

  if (code === 'PGRST202' || code === 'PGRST205' || code === '42883' || code === '42P01') {
    return true;
  }

  return (
    message.includes('builder_page_layouts_admin') ||
    message.includes('list_builder_page_layouts_admin') ||
    message.includes('load_builder_page_layout_admin') ||
    message.includes('create_builder_page_layout') ||
    message.includes('save_builder_page_layout_draft') ||
    message.includes('delete_builder_page_layout') ||
    message.includes('update_builder_page_layout_title') ||
    message.includes('builder_pages') ||
    message.includes('builder_sections') ||
    message.includes('builder_page_sections')
  );
}

async function ensureBuilderApiSupport(): Promise<boolean> {
  if (builderApiSupported !== null) return builderApiSupported;

  if (!builderApiProbe) {
    builderApiProbe = (async () => {
      const probe = await supabase.rpc('list_builder_page_layouts_admin');
      if (!probe.error) {
        builderApiSupported = true;
        cacheBuilderApiSupport(true);
        return true;
      }

      if (isBuilderApiMissingError(probe.error)) {
        builderApiSupported = false;
        cacheBuilderApiSupport(false);
        return false;
      }

      // If the function exists but returns another runtime error, keep builder mode enabled.
      builderApiSupported = true;
      cacheBuilderApiSupport(true);
      return true;
    })();
  }

  try {
    return await builderApiProbe;
  } finally {
    builderApiProbe = null;
  }
}

function firstRow<T>(value: unknown): T | null {
  if (Array.isArray(value)) {
    return (value[0] ?? null) as T | null;
  }
  return (value ?? null) as T | null;
}

function toSingleResponse<T>(result: { data: unknown; error: any }) {
  return {
    data: result.error ? null : firstRow<T>(result.data),
    error: result.error,
  };
}

async function listBuilderLayouts() {
  const result = await supabase.rpc('list_builder_page_layouts_admin');
  if (result.error) {
    return {
      data: null,
      error: result.error,
    };
  }
  return {
    data: (Array.isArray(result.data) ? result.data : []) as PageLayoutRecord<HomepageLayoutContent>[],
    error: null,
  };
}

async function loadBuilderLayout(pageKey: string) {
  const result = await supabase.rpc('load_builder_page_layout_admin', {
    p_page_key: pageKey,
  });
  return toSingleResponse<PageLayoutRecord<HomepageLayoutContent>>(result);
}

async function createBuilderLayout(params: {
  pageKey: string;
  title: string;
  draftContent: HomepageLayoutContent;
  publishedContent?: HomepageLayoutContent | null;
}) {
  const result = await supabase.rpc('create_builder_page_layout', {
    p_page_key: params.pageKey,
    p_title: params.title,
    p_draft_content: params.draftContent,
    p_published_content: params.publishedContent ?? EMPTY_LAYOUT_CONTENT,
  });
  return toSingleResponse<PageLayoutRecord<HomepageLayoutContent>>(result);
}

async function saveBuilderLayoutDraft(
  pageKey: string,
  layout: HomepageLayoutContent
) {
  const result = await supabase.rpc('save_builder_page_layout_draft', {
    p_page_key: pageKey,
    p_layout: layout,
    p_title: null,
  });
  return toSingleResponse<PageLayoutRecord<HomepageLayoutContent>>(result);
}

async function deleteBuilderLayout(pageKey: string) {
  return supabase.rpc('delete_builder_page_layout', {
    p_page_key: pageKey,
  });
}

async function updateBuilderLayoutTitle(pageKey: string, title: string) {
  const result = await supabase.rpc('update_builder_page_layout_title', {
    p_page_key: pageKey,
    p_title: title,
  });
  return toSingleResponse<PageLayoutRecord<HomepageLayoutContent>>(result);
}

function resolvePageLayoutDraft<T>(
  row: PageLayoutRecord<T> | null | undefined,
  fallback: T
): T {
  return (row?.draft_content ?? row?.published_content ?? fallback) as T;
}

function resolvePageLayoutPublished<T>(
  row: PageLayoutRecord<T> | null | undefined,
  fallback: T
): T {
  const published = row?.published_content;
  if (published != null) return published as T;

  if (import.meta.env.DEV) {
    return (row?.draft_content ?? fallback) as T;
  }

  return fallback;
}

function normalizeLayoutForMode(
  value: unknown,
  pageKey: string,
  mode: 'load' | 'save' | 'publish'
): HomepageLayoutContent {
  const validation = validateAndNormalizeHomepageLayout(value, {
    mode,
    pageKey,
    includeDisabled: true,
    fallback: DEFAULT_HOME_LAYOUT_CONTENT,
  });

  if (validation.fatal) {
    throw new Error(
      `Layout validation failed: ${validation.issues.join(' | ') || 'Unknown validation error'}`
    );
  }

  return validation.layout;
}

function layoutsEqual(left: HomepageLayoutContent, right: HomepageLayoutContent) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function normalizeLayoutError(
  error: unknown,
  fallback = 'Layout validation failed'
) {
  const message =
    error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string'
      ? (error as any).message
      : fallback;

  return {
    message,
    code: 'LAYOUT_VALIDATION_FAILED',
  };
}

function normalizeAdminLayoutRecord(
  row: PageLayoutRecord<HomepageLayoutContent>
): PageLayoutRecord<HomepageLayoutContent> {
  return {
    ...row,
    draft_content: normalizeLayoutForMode(
      row.draft_content ?? row.published_content ?? DEFAULT_HOME_LAYOUT_CONTENT,
      row.page_key,
      'load'
    ),
    published_content:
      row.published_content == null
        ? null
        : normalizeLayoutForMode(row.published_content, row.page_key, 'load'),
  };
}

async function listLegacyLayouts() {
  return supabase
    .from('page_layouts')
    .select(PAGE_LAYOUTS_SELECT_LEGACY)
    .order('created_at', { ascending: true })
    .returns<PageLayoutRecord<HomepageLayoutContent>[]>();
}

async function loadLegacyLayout(pageKey: string) {
  return supabase
    .from('page_layouts')
    .select(PAGE_LAYOUTS_SELECT_LEGACY)
    .eq('page_key', pageKey)
    .single<PageLayoutRecord<HomepageLayoutContent>>();
}

async function createLegacyLayout(params: {
  pageKey: string;
  draftContent: HomepageLayoutContent;
  publishedContent?: HomepageLayoutContent | null;
  nowIso?: string;
}) {
  const nowIso = params.nowIso ?? new Date().toISOString();
  return supabase
    .from('page_layouts')
    .insert({
      page_key: params.pageKey,
      draft_content: params.draftContent,
      published_content: params.publishedContent ?? EMPTY_LAYOUT_CONTENT,
      draft_updated_at: nowIso,
      last_published_at: params.publishedContent ? nowIso : null,
    })
    .select(PAGE_LAYOUTS_SELECT_LEGACY)
    .single<PageLayoutRecord<HomepageLayoutContent>>();
}

async function saveLegacyLayoutDraft(
  pageKey: string,
  layout: HomepageLayoutContent,
  nowIso = new Date().toISOString()
) {
  const updateResult = await supabase
    .from('page_layouts')
    .update({
      draft_content: layout,
      draft_updated_at: nowIso,
    })
    .eq('page_key', pageKey)
    .select(PAGE_LAYOUTS_SELECT_LEGACY)
    .maybeSingle<PageLayoutRecord<HomepageLayoutContent>>();

  if (updateResult.error) {
    return {
      data: null,
      error: updateResult.error,
    };
  }

  if (updateResult.data) {
    return {
      data: updateResult.data,
      error: null,
    };
  }

  return supabase
    .from('page_layouts')
    .insert({
      page_key: pageKey,
      draft_content: layout,
      published_content: EMPTY_LAYOUT_CONTENT,
      draft_updated_at: nowIso,
      last_published_at: null,
    })
    .select(PAGE_LAYOUTS_SELECT_LEGACY)
    .single<PageLayoutRecord<HomepageLayoutContent>>();
}

export { resolvePageLayoutDraft, resolvePageLayoutPublished };

export async function listPageLayoutsForAdmin() {
  if (await ensureBuilderApiSupport()) {
    const builderRes = await listBuilderLayouts();
    if (!builderRes.error) {
      return {
        data: (builderRes.data ?? []).map(normalizeAdminLayoutRecord),
        error: null,
      };
    }
    if (!isBuilderApiMissingError(builderRes.error)) return builderRes;
    builderApiSupported = false;
    cacheBuilderApiSupport(false);
  }

  const legacyRes = await listLegacyLayouts();
  if (legacyRes.error || !legacyRes.data) return legacyRes;
  return {
    data: legacyRes.data.map(normalizeAdminLayoutRecord),
    error: null,
  };
}

export async function loadPageLayoutForAdmin(pageKey: string) {
  if (await ensureBuilderApiSupport()) {
    const builderRes = await loadBuilderLayout(pageKey);
    if (!builderRes.error && builderRes.data) {
      return {
        data: normalizeAdminLayoutRecord(builderRes.data),
        error: null,
      };
    }
    if (!builderRes.error) return builderRes;
    if (!isBuilderApiMissingError(builderRes.error)) return builderRes;
    builderApiSupported = false;
    cacheBuilderApiSupport(false);
  }

  const legacyRes = await loadLegacyLayout(pageKey);
  if (legacyRes.error || !legacyRes.data) return legacyRes;
  return {
    data: normalizeAdminLayoutRecord(legacyRes.data),
    error: null,
  };
}

export async function createPageLayout(params: {
  pageKey: string;
  title: string;
  draftContent: HomepageLayoutContent;
  publishedContent?: HomepageLayoutContent | null;
  nowIso?: string;
}) {
  let validatedDraftContent: HomepageLayoutContent;
  let validatedPublishedContent: HomepageLayoutContent | null;
  try {
    validatedDraftContent = normalizeLayoutForMode(params.draftContent, params.pageKey, 'save');
    validatedPublishedContent =
      params.publishedContent == null
        ? EMPTY_LAYOUT_CONTENT
        : normalizeLayoutForMode(params.publishedContent, params.pageKey, 'save');
  } catch (error) {
    return {
      data: null,
      error: normalizeLayoutError(error),
    };
  }

  if (await ensureBuilderApiSupport()) {
    const builderRes = await createBuilderLayout({
      ...params,
      draftContent: validatedDraftContent,
      publishedContent: validatedPublishedContent,
    });
    if (!builderRes.error && builderRes.data) {
      return {
        data: normalizeAdminLayoutRecord(builderRes.data),
        error: null,
      };
    }
    if (!builderRes.error) return builderRes;
    if (!isBuilderApiMissingError(builderRes.error)) return builderRes;
    builderApiSupported = false;
    cacheBuilderApiSupport(false);
  }

  const legacyRes = await createLegacyLayout({
    ...params,
    draftContent: validatedDraftContent,
    publishedContent: validatedPublishedContent,
  });
  if (legacyRes.error || !legacyRes.data) return legacyRes;
  return {
    data: normalizeAdminLayoutRecord(legacyRes.data),
    error: null,
  };
}

export async function deletePageLayout(pageKey: string) {
  if (await ensureBuilderApiSupport()) {
    const builderRes = await deleteBuilderLayout(pageKey);
    if (!builderRes.error) return builderRes;
    if (!isBuilderApiMissingError(builderRes.error)) return builderRes;
    builderApiSupported = false;
    cacheBuilderApiSupport(false);
  }

  return supabase
    .from('page_layouts')
    .delete()
    .eq('page_key', pageKey);
}

export async function savePageLayoutDraft(
  pageKey: string,
  layout: HomepageLayoutContent,
  nowIso = new Date().toISOString()
) {
  let validatedLayout: HomepageLayoutContent;
  try {
    validatedLayout = normalizeLayoutForMode(layout, pageKey, 'save');
  } catch (error) {
    return {
      data: null,
      error: normalizeLayoutError(error),
    };
  }

  if (await ensureBuilderApiSupport()) {
    const builderRes = await saveBuilderLayoutDraft(pageKey, validatedLayout);
    if (!builderRes.error && builderRes.data) {
      return {
        data: normalizeAdminLayoutRecord(builderRes.data),
        error: null,
      };
    }
    if (!builderRes.error) return builderRes;
    if (!isBuilderApiMissingError(builderRes.error)) return builderRes;
    builderApiSupported = false;
    cacheBuilderApiSupport(false);
  }

  const legacyRes = await saveLegacyLayoutDraft(pageKey, validatedLayout, nowIso);
  if (legacyRes.error || !legacyRes.data) return legacyRes;
  return {
    data: normalizeAdminLayoutRecord(legacyRes.data),
    error: null,
  };
}

export async function publishPageLayoutDraft(pageKey: string) {
  const loadResult = await loadPageLayoutForAdmin(pageKey);
  if (loadResult.error || !loadResult.data) {
    return {
      data: null,
      error: loadResult.error ?? { message: `Unable to load layout draft for ${pageKey}` },
    };
  }

  let validatedDraft: HomepageLayoutContent;
  try {
    validatedDraft = normalizeLayoutForMode(
      loadResult.data.draft_content ?? loadResult.data.published_content ?? DEFAULT_HOME_LAYOUT_CONTENT,
      pageKey,
      'publish'
    );
  } catch (error) {
    return {
      data: null,
      error: normalizeLayoutError(error),
    };
  }

  const currentDraft =
    (loadResult.data.draft_content as HomepageLayoutContent | null | undefined) ??
    loadResult.data.published_content ??
    DEFAULT_HOME_LAYOUT_CONTENT;

  if (!layoutsEqual(validatedDraft, currentDraft)) {
    const saveRes = await savePageLayoutDraft(pageKey, validatedDraft);
    if (saveRes.error) {
      return {
        data: null,
        error: saveRes.error,
      };
    }
  }

  return supabase.rpc('publish_page_layout_key', { p_page_key: pageKey });
}

export async function revertPageLayoutDraftToPublished(pageKey: string) {
  return supabase.rpc('revert_page_layout_draft_to_published', { p_page_key: pageKey });
}

export async function updatePageLayoutTitle(pageKey: string, title: string) {
  if (await ensureBuilderApiSupport()) {
    const builderRes = await updateBuilderLayoutTitle(pageKey, title);
    if (!builderRes.error) return builderRes;
    if (!isBuilderApiMissingError(builderRes.error)) return builderRes;
    builderApiSupported = false;
    cacheBuilderApiSupport(false);
  }

  // Legacy table may not have title column in local environments.
  return loadLegacyLayout(pageKey);
}

export async function loadHomePageLayoutForAdmin() {
  return loadPageLayoutForAdmin('home');
}

export async function saveHomePageLayoutDraft(layout: HomepageLayoutContent, nowIso = new Date().toISOString()) {
  return savePageLayoutDraft('home', layout, nowIso);
}

export async function publishHomePageLayoutDraft() {
  return publishPageLayoutDraft('home');
}

export async function revertHomePageLayoutDraftToPublished() {
  return revertPageLayoutDraftToPublished('home');
}

export const DEFAULT_HOME_LAYOUT = DEFAULT_HOME_LAYOUT_CONTENT;
